import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Stack, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { truncMid } from "@/lib/format";
import { CopyBtn } from "@/components/primitives";
import { AssetName, TokenLogo, EmptyState, SkeletonRows, fmtQtyMeta } from "@/components/assets/parts";
import { useRequestMeta, useTokenMeta } from "@/components/assets/tokenMeta";

const COUNT = 25;

// Slide-over listing every asset minted under a policy id (/assets/policy/{id}).
export const PolicyDrawer = ({ policyId, onClose, onPickAsset }) => {
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setPage(1); setRows(null); }, [policyId]);

  useEffect(() => {
    if (!policyId) return undefined;
    let m = true;
    setLoading(true);
    setError(null);
    http
      .get(`${API}/assets/policy/${policyId}`, { params: { count: COUNT, page }, timeout: 30000 })
      .then((r) => { if (m) setRows(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (m) setError(e?.response?.status === 503 ? "Policy lookup is temporarily unavailable." : "Couldn't load assets for this policy."); })
      .finally(() => { if (m) setLoading(false); });
    return () => { m = false; };
  }, [policyId, page]);

  useEffect(() => {
    if (!policyId) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [policyId, onClose]);

  useRequestMeta((rows || []).map((x) => x.asset));
  const { meta } = useTokenMeta();

  if (!policyId) return null;

  const items = (rows || []).map((x) => ({
    asset: x.asset,
    quantity: x.quantity,
    asset_name: (x.asset || "").slice(56),
    policy_id: (x.asset || "").slice(0, 56),
  }));

  return createPortal(
    <>
      <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="policy-drawer-overlay" />
      <aside data-testid="policy-drawer" className="fixed top-0 right-0 bottom-0 w-full sm:w-[460px] z-[70] bg-background border-l border-primary/25 flex flex-col">
        <div className="px-5 py-3 border-b border-primary/15 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#22d3ee] mono"><Stack size={14} weight="duotone" /> Policy assets</span>
          <button type="button" data-testid="policy-drawer-close" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground"><X size={16} /></button>
        </div>
        <div className="px-5 py-2 border-b border-primary/10 flex items-center gap-2 text-[11px] mono text-muted-foreground">
          <span className="truncate">{truncMid(policyId, 14, 8)}</span>
          <CopyBtn value={policyId} label="policy" />
        </div>

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-[13px] mono tabnum">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                <th className="text-left font-sans font-medium px-4 py-2.5 border-b border-primary/10">Asset</th>
                <th className="text-right font-sans font-medium px-4 py-2.5 border-b border-primary/10">Quantity</th>
              </tr>
            </thead>
            {loading && !items.length ? (
              <SkeletonRows rows={10} cols={2} />
            ) : (
              <tbody>
                {items.map((it, i) => (
                  <tr key={it.asset} data-testid={`policy-asset-${i}`} className="border-b border-primary/5 last:border-b-0 hover:bg-primary/5 transition-colors">
                    <td className="px-4 py-2.5"><div className="flex items-center gap-2 min-w-0 max-w-[230px] sm:max-w-[300px]"><TokenLogo item={it} size={22} /><AssetName item={it} onPick={(a) => { onPickAsset(a); onClose(); }} pin={false} testid={`policy-asset-link-${i}`} /></div></td>
                    <td className="px-4 py-2.5 text-right text-foreground/90 whitespace-nowrap">{fmtQtyMeta(it.quantity, meta.get(it.asset))}</td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
          {error ? <div data-testid="policy-error" className="px-5 py-6 text-center text-[12px] text-rose-400 mono">{error}</div> : null}
          {!loading && !error && !items.length ? <EmptyState label="No assets found for this policy." testid="policy-empty" /> : null}
        </div>

        <div className="px-5 py-2.5 border-t border-primary/10 flex items-center justify-end gap-2">
          <button type="button" data-testid="policy-prev" disabled={page <= 1 || loading} onClick={() => setPage((p) => Math.max(1, p - 1))} className="inline-flex items-center gap-1 px-2.5 py-1 border border-primary/20 rounded text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground disabled:opacity-40"><CaretLeft size={11} /> Prev</button>
          <span data-testid="policy-page" className="text-[11px] mono text-muted-foreground tabnum px-1">{page}</span>
          <button type="button" data-testid="policy-next" disabled={items.length < COUNT || loading} onClick={() => setPage((p) => p + 1)} className="inline-flex items-center gap-1 px-2.5 py-1 border border-primary/20 rounded text-[10px] uppercase tracking-[0.15em] mono text-muted-foreground hover:text-foreground disabled:opacity-40">Next <CaretRight size={11} /></button>
        </div>
      </aside>
    </>,
    document.body,
  );
};
